import { useCallback, useEffect, useMemo, useState } from 'react';
import type { PayoutResponse, StoreResponse } from '@clarity/contract';
import { usePayouts } from '../../queries/index.js';
import { useScopeContext } from '../../lib/scopeContext.js';
import { useI18n } from '../../lib/i18n.js';
import { isOnboardingRequired, money } from '../format.js';
import { PayoutsView } from '../store/shared.js';
import { StorePayouts } from '../store/StorePayouts.js';
import { OnboardingNote, splitOnboarded } from './shared.js';

/**
 * CombinedPayouts (clarity-navigation-scope task 6.2, Req 6.1–6.4, design §7) —
 * the Payouts flexible surface at an All / Brand scope spanning MORE than one
 * branch. It presents a **section per in-scope onboarded branch**, each reusing
 * the UNCHANGED single-store {@link PayoutsView} (its payout blocks + the
 * expand-to-reconcile rows), with a scope-level **roll-up** across branches
 * where a total is meaningful (total net payout + total gross sales).
 *
 * React-safe fan-out (same pattern as {@link CombinedOrders} /
 * {@link CombinedStatements}): each branch gets its own child — the visible
 * section ({@link BranchPayoutsSection}, embedding `PayoutsView`) plus a hidden
 * source ({@link BranchPayoutSource}) calling `usePayouts(branch.id)` and
 * reporting its totals up for the roll-up. Both share the SAME per-branch query
 * key, so React Query dedupes — no double fetch. Per-branch loading/error stays
 * ISOLATED inside each section (Req 6.3).
 *
 * The per-branch reconciliation EXPAND (`PayoutsView`'s expand-a-payout row)
 * stays LOCAL to each branch section here — the shared `?payout=` URL param
 * would collide across branches, so each combined section manages its own expand
 * state. The single-branch passthrough keeps the URL-driven `?payout=` expand
 * (the standalone {@link StorePayouts} page handles it), so the order→payout
 * trace is unregressed for the single-store case (Req 8.4).
 *
 * These are cheap per-branch DB reads (not portal fetches; design §7 / Req 6.5),
 * sharing the `queryKeys.payouts` cache with the single-store view.
 *
 * Single branch (Req 6.4): a one-branch scope renders the standalone single-store
 * Payouts page ({@link StorePayouts}) — no "all" chrome, URL-driven `?payout=`
 * expand preserved.
 */

/** A shared frozen empty-rows reference, so the no-data case is stable across renders. */
const EMPTY_ROWS: readonly PayoutResponse[] = Object.freeze([]);

/** The per-branch read state a {@link BranchPayoutSource} reports up for the roll-up. */
interface BranchResult {
  isLoading: boolean;
  /** A genuine transport failure (NOT the expected `409 onboarding_required`). */
  isError: boolean;
  rows: readonly PayoutResponse[];
}

export function CombinedPayouts() {
  const { scope, branches, isSingleBranch } = useScopeContext();

  // Req 6.4 — a one-branch scope renders the standalone single-store Payouts
  // page, keeping the `?payout=` expand sub-state (StorePayouts owns it).
  if (isSingleBranch && branches.length === 1) {
    return (
      <section data-surface="payouts" data-scope={scope.kind}>
        <StorePayouts store={branches[0] as StoreResponse} />
      </section>
    );
  }

  return <CombinedPayoutsBody />;
}

/**
 * The multi-branch combined body. Split from {@link CombinedPayouts} so the
 * single-branch passthrough never mounts the fan-out machinery and the hook
 * count stays stable for a given in-scope branch set.
 */
function CombinedPayoutsBody() {
  const { scope, branches } = useScopeContext();
  const { t } = useI18n();

  // Only ONBOARDED branches contribute data reads (Req 6.2); the rest are counted.
  const { onboarded, stillOnboarding } = useMemo(
    () => splitOnboarded(branches),
    [branches],
  );

  const [results, setResults] = useState<Record<string, BranchResult>>({});

  const report = useCallback((branchId: string, result: BranchResult) => {
    setResults((prev) => {
      const existing = prev[branchId];
      if (
        existing &&
        existing.isLoading === result.isLoading &&
        existing.isError === result.isError &&
        existing.rows === result.rows
      ) {
        return prev;
      }
      return { ...prev, [branchId]: result };
    });
  }, []);

  // The scope-level roll-up (Req 6.1): Σ net payout + Σ gross sales across every
  // onboarded branch's payouts — both additive, so a total is meaningful.
  const rollup = useMemo(() => {
    let totalNetPayout = 0;
    let totalGrossSales = 0;
    let payouts = 0;
    for (const branch of onboarded) {
      const result = results[branch.id];
      if (!result) continue;
      for (const p of result.rows) {
        totalNetPayout += p.netPayout;
        totalGrossSales += p.grossSales;
        payouts += 1;
      }
    }
    return { totalNetPayout, totalGrossSales, payouts };
  }, [onboarded, results]);

  const scopeNoBranches = onboarded.length === 0;

  return (
    <section data-surface="payouts" data-scope={scope.kind} className="combined-view">
      {onboarded.map((branch) => (
        <BranchPayoutSource key={branch.id} branch={branch} onResult={report} />
      ))}

      {stillOnboarding > 0 && <OnboardingNote count={stillOnboarding} t={t} />}

      {scopeNoBranches ? (
        <p className="muted">
          {stillOnboarding > 0
            ? 'All in-scope branches are still onboarding — no payouts to combine yet.'
            : 'No branches in scope.'}
        </p>
      ) : (
        <>
          {rollup.payouts > 0 && (
            <table
              className="data-table combined-view__rollup"
              aria-label="Payouts roll-up"
            >
              <tbody>
                <tr>
                  <th scope="row">Total gross sales</th>
                  <td>{money(rollup.totalGrossSales)}</td>
                </tr>
                <tr>
                  <th scope="row">Total net payout</th>
                  <td>{money(rollup.totalNetPayout)}</td>
                </tr>
              </tbody>
            </table>
          )}

          {onboarded.map((branch) => (
            <BranchPayoutsSection key={branch.id} branch={branch} />
          ))}
        </>
      )}
    </section>
  );
}

/**
 * One visible per-branch section: a branch heading + the UNCHANGED single-store
 * {@link PayoutsView} (Req 6.4 reuse). The reconciliation EXPAND is held LOCAL
 * here (not via the shared `?payout=` URL param, which would collide across
 * branches) so each branch section expands independently. `PayoutsView` owns its
 * own loading/error/empty handling, so a failing/loading branch shows that
 * inside its own section — isolated from the others (Req 6.3).
 */
function BranchPayoutsSection({ branch }: { branch: StoreResponse }) {
  const [openPayoutId, setOpenPayoutId] = useState<string | null>(null);
  return (
    <section className="combined-view__branch" aria-label={branch.name ?? branch.externalStoreId}>
      <h3 className="combined-view__branch-head">
        <span className="combined-view__store">{branch.name ?? branch.externalStoreId}</span>
        <span className="cell-sub"> {branch.externalStoreId}</span>
      </h3>
      {/*
       * Req 6.1–6.4 — the combined surface renders one PayoutsView per in-scope
       * branch and DELIBERATELY OMITS the opt-in props. We pass only `store` +
       * the local `openPayoutId`/`onTogglePayout` expand state; we do NOT pass
       * `withFilter`, `onOpenPayout`, `from`, `to`, or `onRangeChange`. Those
       * default off (no paid-at filter, no per-row Details modal affordance), so
       * each branch renders READ-ONLY: just the month-grouped panel + its local
       * expand state, feeding the scope roll-up (BranchPayoutSource → usePayouts).
       * The standalone StorePayouts page is the one that opts into the filter and
       * Details; multi-branch keeps per-branch local `openPayoutId` because the
       * shared `?payout=` URL param would collide across branches. Do NOT add the
       * opt-in props here — that would regress the read-only combined behavior.
       */}
      <PayoutsView store={branch} openPayoutId={openPayoutId} onTogglePayout={setOpenPayoutId} />
    </section>
  );
}

/**
 * One hidden per-branch read source feeding the roll-up (design §7). It calls
 * the existing {@link usePayouts} hook for ONE branch and reports its totals up
 * via `onResult`; it renders nothing. The expected `409 onboarding_required` is
 * NOT a failure (defense-in-depth): it reports empty rows and no error.
 */
function BranchPayoutSource({
  branch,
  onResult,
}: {
  branch: StoreResponse;
  onResult: (branchId: string, result: BranchResult) => void;
}) {
  const payouts = usePayouts(branch.id);
  const onboardingBlocked = isOnboardingRequired(payouts.error);
  const isError = payouts.isError && !onboardingBlocked;
  const { isLoading } = payouts;
  const rows: readonly PayoutResponse[] =
    payouts.data?.status === 200 && !onboardingBlocked ? payouts.data.body : EMPTY_ROWS;

  useEffect(() => {
    onResult(branch.id, { isLoading, isError, rows });
  }, [branch.id, isLoading, isError, rows, onResult]);

  return null;
}
